import { imagesApi } from '../../api';
import { formatMs } from '../../utils/format';
import './search.css';

/**
 * Similar images, as a grid of thumbnails with their similarity.
 *
 * Similarity is printed as a bare number, deliberately. It is a cosine distance
 * rescaled to 0–1, not a confidence, and a bar or a row of stars would invite
 * reading it as one — the same reason there is no confidence bar on an extracted
 * attribute. The figure is monospaced and tabular so a column of them can be
 * compared down the page.
 */
export function ImageResults({ results, meta, onPick, activeId = null }) {
  return (
    <>
      <div className="results__bar">
        <span className="results__count">
          <b>{meta?.count ?? results.length}</b> image
          {(meta?.count ?? results.length) === 1 ? '' : 's'}
          {meta?.metric ? ` · ${meta.metric.toLowerCase()} similarity` : ''}
          {meta?.elapsedMs !== undefined ? ` · ${formatMs(meta.elapsedMs)}` : ''}
        </span>
      </div>

      <div className="sgrid">
        {results.map((hit) => (
          <ImageTile
            key={hit.image_id}
            hit={hit}
            onPick={onPick}
            active={hit.image_id === activeId}
          />
        ))}
      </div>
    </>
  );
}

function ImageTile({ hit, onPick, active }) {
  const dimensions = hit.width && hit.height ? `${hit.width}×${hit.height}` : null;

  return (
    <article className={`stile ${active ? 'stile--active' : ''}`}>
      <button
        type="button"
        className="stile__img"
        onClick={() => onPick?.(hit)}
        title="Search for images like this one"
      >
        {/* The thumbnail, not the full frame: a grid of twenty 1920×1080 JPEGs is
            tens of megabytes, and the endpoint already serves a small variant. */}
        <img src={imagesApi.fileUrl(hit.image_id, { thumb: true })} alt={hit.filename} />
        <span className="stile__score" title="Cosine similarity, not a confidence">
          {formatSimilarity(hit.similarity)}
        </span>
      </button>
      <div className="stile__body">
        <div className="stile__name" title={hit.filename}>
          {hit.filename || hit.image_id}
        </div>
        <div className="stile__meta">
          {hit.image_id}
          {dimensions ? ` · ${dimensions}` : ''}
        </div>
      </div>
    </article>
  );
}

/** Three decimals, always — a column of ragged precision is hard to scan. */
export function formatSimilarity(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(3) : '—';
}
